/**
 * Constantes del dominio del Tablero CDTEC.
 *
 * Es la única fuente de verdad de los estados, tipos, prioridades y roles. La API los usa para
 * validar y la web para dibujar columnas y filtros. Si un valor cambia aquí, cambia en los dos lados.
 *
 * Los valores internos van en MAYÚSCULAS sin tildes (se guardan así en la base de datos);
 * las etiquetas en español para las personas están al final del archivo.
 */

/**
 * Etapas por las que pasa una tarea, en orden. El orden define las columnas del tablero.
 * Los dos últimos son **cerrados** (terminales): `HECHO` (se terminó) y `DESCARTADA` (no se hará, con su
 * motivo; ADR-0017). En el tablero las dos van juntas en la columna «Cerradas».
 */
export const ESTADOS = [
  'NUEVO',
  'POR_ACLARAR',
  'POR_HACER',
  'EN_CURSO',
  'EN_REVISION',
  'EN_STAGING',
  'PARA_PRODUCCION',
  'HECHO',
  'DESCARTADA',
] as const;
export type Estado = (typeof ESTADOS)[number];

/** Estados cerrados: no cuentan como abiertos (resumen, aviso de carga) y por defecto no se listan. */
export const ESTADOS_CERRADOS = ['HECHO', 'DESCARTADA'] as const satisfies readonly Estado[];
export type EstadoCerrado = (typeof ESTADOS_CERRADOS)[number];

/** Estados del flujo de trabajo (todos menos `DESCARTADA`, que se elige aparte y con su motivo). */
export type EstadoDelFlujo = Exclude<Estado, 'DESCARTADA'>;

/** Estados del flujo de trabajo, en orden. Con ellos se crea una tarea y se mueve con ← y →. */
export const ESTADOS_DEL_FLUJO = ESTADOS.filter(
  (estado): estado is EstadoDelFlujo => estado !== 'DESCARTADA',
);

/** Estados abiertos: los que aún están en el flujo (todos menos `HECHO` y `DESCARTADA`). */
export const ESTADOS_ABIERTOS = ESTADOS.filter((estado) => !esEstadoCerrado(estado));

/** ¿Está cerrada una tarea con este estado (hecha o descartada)? */
export function esEstadoCerrado(estado: Estado): estado is EstadoCerrado {
  return (ESTADOS_CERRADOS as readonly Estado[]).includes(estado);
}

/**
 * Motivo por el que se descarta una tarea. `DUPLICADA` exige la tarea original (que exista);
 * los demás exigen un detalle escrito.
 */
export const MOTIVOS_DESCARTE = ['NO_POR_AHORA', 'NO_ES_VIABLE', 'DUPLICADA', 'OTRO'] as const;
export type MotivoDescarte = (typeof MOTIVOS_DESCARTE)[number];

/** Largo mínimo del detalle de un descarte (salvo en «Duplicada», donde es opcional). */
export const DETALLE_DESCARTE_MINIMO = 5;

/** Naturaleza de la tarea: define la plantilla y cómo se prioriza. */
export const TIPOS = ['REQUERIMIENTO', 'ERROR', 'DEUDA_TECNICA', 'CONSULTA'] as const;
export type Tipo = (typeof TIPOS)[number];

/**
 * Urgencia. P0: plataforma caída, riego en riesgo o clave expuesta (el mismo día).
 * P1: dato incorrecto o clientes afectados (la semana). P2: planificada. P3: deseable.
 */
export const PRIORIDADES = ['P0', 'P1', 'P2', 'P3'] as const;
export type Prioridad = (typeof PRIORIDADES)[number];

/** Esfuerzo estimado. Una tarea «Grande» se parte antes de empezar. */
export const ESFUERZOS = ['RAPIDA', 'MEDIA', 'GRANDE'] as const;
export type Esfuerzo = (typeof ESFUERZOS)[number];

/** Qué puede hacer cada persona. ADMIN > DESARROLLO > LECTURA. */
export const ROLES = ['ADMIN', 'DESARROLLO', 'LECTURA'] as const;
export type Rol = (typeof ROLES)[number];

/** Clases de movimientos que quedan en el historial de una tarea. */
export const TIPOS_EVENTO = [
  'CREADA',
  'ESTADO',
  'PRIORIDAD',
  'RESPONSABLE',
  'EDITADA',
  'NOTA',
  'ELIMINADA',
  'ADJUNTO',
  'ADJUNTO_ELIMINADO',
  /** Cambio de relación con otra tarea: entrar o salir como subtarea, o agregar o quitar un bloqueo (ADR-0018). */
  'RELACION',
] as const;
export type TipoEvento = (typeof TIPOS_EVENTO)[number];

/** Máximo de tareas «En curso» por persona antes de avisar (regla del equipo). */
export const LIMITE_EN_CURSO_POR_PERSONA = 2;

/** Prefijo del número legible de una tarea: la tarea 12 se muestra como «CDT-12». */
export const PREFIJO_NUMERO = 'CDT';

/**
 * Nombres con que se muestran las prioridades por defecto. En la base, la API y los tokens las
 * prioridades siguen siendo P0..P3; un administrador puede renombrar lo que se ve (ver configuracion.ts).
 */
export const ETIQUETA_PRIORIDAD_POR_DEFECTO: Record<Prioridad, string> = {
  P0: 'Urgente',
  P1: 'Alta',
  P2: 'Media',
  P3: 'Baja',
};

/** Etiquetas en español para mostrar en pantalla. */
export const ETIQUETA_ESTADO: Record<Estado, string> = {
  NUEVO: 'Nuevo',
  POR_ACLARAR: 'Por aclarar',
  POR_HACER: 'Por hacer',
  EN_CURSO: 'En curso',
  EN_REVISION: 'En revisión',
  EN_STAGING: 'En staging',
  PARA_PRODUCCION: 'Para producción',
  HECHO: 'Hecho',
  DESCARTADA: 'Descartada',
};

export const ETIQUETA_MOTIVO_DESCARTE: Record<MotivoDescarte, string> = {
  NO_POR_AHORA: 'No por ahora',
  NO_ES_VIABLE: 'No es viable',
  DUPLICADA: 'Duplicada',
  OTRO: 'Otro',
};

export const ETIQUETA_TIPO: Record<Tipo, string> = {
  REQUERIMIENTO: 'Requerimiento',
  ERROR: 'Bug',
  DEUDA_TECNICA: 'Deuda técnica',
  CONSULTA: 'Consulta',
};

export const ETIQUETA_ESFUERZO: Record<Esfuerzo, string> = {
  RAPIDA: 'Rápida',
  MEDIA: 'Media',
  GRANDE: 'Grande',
};

export const DESCRIPCION_ESFUERZO: Record<Esfuerzo, string> = {
  RAPIDA: 'Un ajuste puntual',
  MEDIA: 'Varias cosas juntas',
  GRANDE: 'Requiere dividirla',
};

export const ETIQUETA_ROL: Record<Rol, string> = {
  ADMIN: 'Administrador',
  DESARROLLO: 'Desarrollo',
  LECTURA: 'Solo lectura',
};

/** Número legible de una tarea, p. ej. `formatearNumero(12)` da `CDT-12`. */
export function formatearNumero(numero: number): string {
  return `${PREFIJO_NUMERO}-${numero}`;
}

/** Lee «CDT-12», «cdt 12» o «12» y devuelve 12; `null` si no es un número de tarea. */
export function leerNumeroTarea(texto: string): number | null {
  const coincidencia = /^\s*(?:cdt[-\s]?)?(\d{1,9})\s*$/i.exec(texto);
  if (!coincidencia) return null;
  const numero = Number(coincidencia[1]);
  return numero > 0 ? numero : null;
}

/**
 * Texto del motivo de una tarea descartada, igual en la web, el historial y el CSV:
 * «No por ahora: somos pocos» o «Duplicada de CDT-1. Lo mismo que la otra».
 */
export function textoMotivoDescarte(descarte: {
  motivo: MotivoDescarte;
  detalle: string;
  duplicadaDe: number | null;
}): string {
  const detalle = descarte.detalle.trim();
  if (descarte.motivo === 'DUPLICADA') {
    const original =
      descarte.duplicadaDe === null ? 'otra tarea' : formatearNumero(descarte.duplicadaDe);
    return `Duplicada de ${original}.${detalle ? ` ${detalle}` : ''}`;
  }
  return `${ETIQUETA_MOTIVO_DESCARTE[descarte.motivo]}: ${detalle}`;
}

/* ------------------------------ Subtareas ------------------------------- */

/**
 * Avance de una tarea madre (ADR-0018): cuántas subtareas hay y cuántas están hechas. Las descartadas no cuentan en
 * el total (no se van a hacer). `null` si no tiene subtareas; `{ total: 0, hechas: 0 }` si todas están descartadas.
 * La API lo calcula para `progresoSubtareas` y la web puede usarlo igual.
 */
export function progresoSubtareas(
  hijas: readonly { estado: Estado }[],
): { total: number; hechas: number } | null {
  if (hijas.length === 0) return null;
  const contables = hijas.filter((hija) => hija.estado !== 'DESCARTADA');
  return {
    total: contables.length,
    hechas: contables.filter((hija) => hija.estado === 'HECHO').length,
  };
}

/** Cuántas subtareas siguen abiertas (ni hechas ni descartadas). */
export function contarSubtareasAbiertas(hijas: readonly { estado: Estado }[]): number {
  return hijas.filter((hija) => !esEstadoCerrado(hija.estado)).length;
}

/** Aviso suave al cerrar una madre con subtareas abiertas (no bloquea): «Quedan 2 subtareas abiertas.». */
export function avisoSubtareasAbiertas(abiertas: number): string {
  return abiertas === 1 ? 'Queda 1 subtarea abierta.' : `Quedan ${abiertas} subtareas abiertas.`;
}

/* ------------------------------- Bloqueos -------------------------------- */

/**
 * Bloqueantes que siguen bloqueando (ADR-0018): los abiertos. Uno cerrado (hecho o descartado) ya no bloquea, aunque la
 * relación se conserve (se muestra tachado). Las tareas eliminadas no llegan aquí (la API no las incluye).
 */
export function bloqueantesAbiertos<T extends { estado: Estado }>(bloqueantes: readonly T[]): T[] {
  return bloqueantes.filter((bloqueante) => !esEstadoCerrado(bloqueante.estado));
}

/** ¿Está bloqueada una tarea con estos bloqueantes? Sí si alguno sigue abierto. La API y la web deciden igual. */
export function estaBloqueada(bloqueantes: readonly { estado: Estado }[]): boolean {
  return bloqueantesAbiertos(bloqueantes).length > 0;
}

/**
 * Estados a los que mover una tarea bloqueada da un aviso (no la frena): empezar a trabajarla, revisarla o darla por
 * hecha. La web pide confirmación con estos mismos estados.
 */
export const ESTADOS_QUE_AVISAN_BLOQUEO = [
  'EN_CURSO',
  'EN_REVISION',
  'HECHO',
] as const satisfies readonly Estado[];

/** ¿Mover a este estado debe avisar si la tarea está bloqueada? */
export function avisaSiBloqueada(estado: Estado): boolean {
  return (ESTADOS_QUE_AVISAN_BLOQUEO as readonly Estado[]).includes(estado);
}

/* ----------------------------- Fecha límite ----------------------------- */

/**
 * Zona horaria del equipo: «hoy» para la fecha límite es el día en Chile, no el del servidor (UTC) ni el del
 * computador de quien mira.
 */
export const ZONA_HORARIA_EQUIPO = 'America/Santiago';

/** Una tarea abierta «vence pronto» si a su fecha límite le faltan estos días o menos (0 = vence hoy). */
export const DIAS_VENCE_PRONTO = 2;

/** Rango aceptado para una fecha límite: solo evita errores de tipeo (año 20026), no es una regla del equipo. */
export const FECHA_LIMITE_MINIMA = '2000-01-01';
export const FECHA_LIMITE_MAXIMA = '2100-12-31';

const FORMATO_DIA_EQUIPO = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONA_HORARIA_EQUIPO,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Día de hoy (`AAAA-MM-DD`) en Chile para el instante `ahora`. Se arma con `formatToParts` (y no con el formato
 * de un idioma) para no depender de cómo cada motor escribe la fecha. Sin librerías: el cambio de horario lo
 * resuelve `Intl` con la base de zonas del motor.
 */
export function hoyEnChile(ahora: Date = new Date()): string {
  const partes = Object.fromEntries(
    FORMATO_DIA_EQUIPO.formatToParts(ahora).map((parte) => [parte.type, parte.value]),
  );
  return `${partes.year ?? ''}-${partes.month ?? ''}-${partes.day ?? ''}`;
}

/** ¿Es `texto` un día real con formato `AAAA-MM-DD` (sin hora)? */
function esDiaReal(texto: string): boolean {
  const coincidencia = /^(\d{4})-(\d{2})-(\d{2})$/.exec(texto);
  if (!coincidencia) return false;
  const [anio, mes, dia] = [coincidencia[1], coincidencia[2], coincidencia[3]].map(Number) as [
    number,
    number,
    number,
  ];
  const fecha = new Date(Date.UTC(anio, mes - 1, dia));
  return (
    fecha.getUTCFullYear() === anio && fecha.getUTCMonth() === mes - 1 && fecha.getUTCDate() === dia
  );
}

/**
 * Valida una fecha límite (`AAAA-MM-DD`, sin hora). Devuelve el mensaje de error en español o `null`. Puede ser
 * pasada (p. ej. al cargar una tarea que ya venció) o futura.
 */
export function errorFechaLimite(texto: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texto))
    return 'La fecha límite debe tener el formato AAAA-MM-DD.';
  if (!esDiaReal(texto)) return 'La fecha límite no es una fecha válida.';
  if (texto < FECHA_LIMITE_MINIMA || texto > FECHA_LIMITE_MAXIMA) {
    return 'La fecha límite debe estar entre los años 2000 y 2100.';
  }
  return null;
}

/** Días que faltan desde `hoy` hasta `fechaLimite` (los dos `AAAA-MM-DD`): 0 si vence hoy, negativo si ya pasó. */
export function diasHastaFechaLimite(fechaLimite: string, hoy: string): number {
  const aDias = (dia: string) => Date.parse(`${dia}T00:00:00.000Z`) / 86_400_000;
  return Math.round(aDias(fechaLimite) - aDias(hoy));
}

/**
 * ¿Está atrasada la tarea? Sí si tiene fecha límite anterior a `hoy` (`AAAA-MM-DD` en Chile, ver `hoyEnChile`) y
 * sigue abierta: una tarea hecha o descartada nunca está atrasada. Vence «hoy» no es atrasada. La API (filtro
 * `?atrasada=`) y la web (tarjeta y lista) deciden igual.
 */
export function estaAtrasada(
  tarea: { fechaLimite: string | null; estado: Estado },
  hoy: string,
): boolean {
  return tarea.fechaLimite !== null && !esEstadoCerrado(tarea.estado) && tarea.fechaLimite < hoy;
}

/** ¿Vence pronto? Abierta y le faltan entre 0 y `DIAS_VENCE_PRONTO` días (hoy incluido). Una atrasada no. */
export function vencePronto(
  tarea: { fechaLimite: string | null; estado: Estado },
  hoy: string,
): boolean {
  if (tarea.fechaLimite === null || esEstadoCerrado(tarea.estado)) return false;
  const dias = diasHastaFechaLimite(tarea.fechaLimite, hoy);
  return dias >= 0 && dias <= DIAS_VENCE_PRONTO;
}

/* ------------------------- Edición por autoría -------------------------- */

/**
 * Campos del «requerimiento» de una tarea: solo quien la creó y un administrador pueden cambiarlos.
 * La API y la web comparten esta lista para decidir lo mismo (la API rechaza, la web deshabilita).
 */
export const CAMPOS_REQUERIMIENTO = [
  'titulo',
  'descripcion',
  'criterio',
  'tipo',
  'prioridad',
  'esfuerzo',
  'pedidoPor',
  'proyectoId',
  'tocaRiego',
  'requiereProduccion',
  'detalleProduccion',
  // Elegir o quitar la tarea madre es parte del requerimiento de la subtarea (ADR-0018): solo se mira la autoría de la hija.
  'padreId',
  // La fecha límite (CDT-76) es un compromiso de quien pidió la tarea: no la mueve cualquiera.
  'fechaLimite',
] as const;
export type CampoRequerimiento = (typeof CAMPOS_REQUERIMIENTO)[number];

/**
 * Campos de colaboración: cualquiera con permiso de escritura los cambia (mover, enlace y el motivo
 * al descartar, que va junto con el estado; ADR-0017).
 */
export const CAMPOS_COLABORACION = ['estado', 'enlace', 'descarte'] as const;
export type CampoColaboracion = (typeof CAMPOS_COLABORACION)[number];

/**
 * Campos que solo cambia un administrador, aunque la persona sea quien creó la tarea:
 * la fecha de inicio (`creadoEn`) y la de cierre (`cerradaEn`), pensadas para cargar tareas de días
 * anteriores. Se pueden dar al crear y al editar; la de cierre solo en tareas «Hecho». Un no administrador que la envíe recibe 403 (en un PATCH mixto se rechaza todo).
 */
export const CAMPOS_SOLO_ADMIN = ['creadoEn', 'cerradaEn'] as const;
export type CampoSoloAdmin = (typeof CAMPOS_SOLO_ADMIN)[number];

/** Mensaje (API y web) cuando alguien que no es administrador intenta fijar la fecha de inicio o de cierre. */
export const MENSAJE_SOLO_ADMIN =
  'Solo un administrador puede cambiar las fechas de inicio y de cierre de una tarea.';

/** Fecha mínima aceptada como fecha de inicio. */
export const FECHA_INICIO_MINIMA = '2000-01-01';

/**
 * Valida una fecha de inicio o de cierre (`YYYY-MM-DD` o ISO con hora). Devuelve el mensaje de error en español,
 * o `null` si es válida. Debe ser una fecha real, no futura y no anterior a 2000-01-01.
 * Se compara el día tal como viene escrito (los primeros 10 caracteres) contra el día UTC de `ahora`:
 * en Chile el día local nunca va por delante del día UTC, así que «hoy» siempre se acepta.
 */
export function errorFechaInicio(texto: string, ahora: Date = new Date()): string | null {
  const textoLimpio = texto.trim();
  const coincidencia = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(textoLimpio);
  if (!coincidencia) return 'La fecha debe tener el formato AAAA-MM-DD.';
  const anio = Number(coincidencia[1]);
  const mes = Number(coincidencia[2]);
  const diaMes = Number(coincidencia[3]);
  const fecha = new Date(Date.UTC(anio, mes - 1, diaMes));
  const esReal =
    fecha.getUTCFullYear() === anio &&
    fecha.getUTCMonth() === mes - 1 &&
    fecha.getUTCDate() === diaMes;
  if (!esReal || Number.isNaN(new Date(textoLimpio).getTime())) return 'La fecha no es válida.';
  const dia10 = textoLimpio.slice(0, 10);
  if (dia10 < FECHA_INICIO_MINIMA) return 'La fecha no puede ser anterior al 1 de enero de 2000.';
  if (dia10 > ahora.toISOString().slice(0, 10)) return 'La fecha no puede ser futura.';
  return null;
}

/**
 * Convierte una fecha de inicio o de cierre válida en el instante que se guarda: mediodía UTC del día elegido
 * (así no cambia de día al mostrarla en hora de Chile). Si el día elegido es hoy, se usa el instante
 * actual para no dejar una fecha en el futuro. Supone que `errorFechaInicio` devolvió `null`.
 */
export function instanteDeFechaInicio(texto: string, ahora: Date = new Date()): Date {
  const mediodia = new Date(`${texto.trim().slice(0, 10)}T12:00:00.000Z`);
  return mediodia.getTime() > ahora.getTime() ? ahora : mediodia;
}

/** Mensaje de un campo rechazado por `cambiosPermitidos`. */
export function mensajeCampoProhibido(campo: string): string {
  return (CAMPOS_SOLO_ADMIN as readonly string[]).includes(campo)
    ? MENSAJE_SOLO_ADMIN
    : MENSAJE_SOLO_AUTOR;
}

/** El responsable es mixto: cualquiera se asigna o suelta a sí mismo; lo demás es del autor y del admin. */
export const CAMPO_RESPONSABLE = 'responsableId';

/** Mensaje que se muestra (API y web) cuando alguien intenta cambiar lo que no le corresponde. */
export const MENSAJE_SOLO_AUTOR =
  'Solo quien creó la tarea o un administrador puede cambiar esto; puedes dejar un comentario.';

/**
 * ¿Puede esta persona editar el requerimiento de la tarea? Sí si es administradora o quien la creó.
 * Si la tarea no tiene creador conocido (`creadaPor` nulo), solo el administrador.
 */
export function puedeEditarRequerimiento(
  usuario: { id: number; rol: Rol },
  tarea: { creadaPor: { id: number } | null },
): boolean {
  if (usuario.rol === 'ADMIN') return true;
  return tarea.creadaPor !== null && tarea.creadaPor.id === usuario.id;
}

export type ResultadoCambiosPermitidos = { ok: true } | { ok: false; campos: string[] };

/**
 * Decide si un conjunto de cambios (como los de un PATCH) es permitido. Es atómico: si un solo campo
 * no se puede, devuelve todos los no permitidos y no se debe aplicar ninguno.
 *
 * El administrador puede todo. El creador puede todo menos los `CAMPOS_SOLO_ADMIN`.
 * Quien no es autor ni admin puede: cambiar `estado` y `enlace`, asignarse la tarea a sí mismo
 * (`responsableId` = su id) y soltarla (`responsableId` = null) solo si él es el responsable actual.
 */
export function cambiosPermitidos(
  usuario: { id: number; rol: Rol },
  tarea: { creadaPor: { id: number } | null; responsable: { id: number } | null },
  cambios: Readonly<Record<string, unknown>>,
): ResultadoCambiosPermitidos {
  if (usuario.rol === 'ADMIN') return { ok: true };

  const campos: string[] = [];
  const esAutor = puedeEditarRequerimiento(usuario, tarea);
  for (const [campo, valor] of Object.entries(cambios)) {
    if (valor === undefined) continue;
    if ((CAMPOS_SOLO_ADMIN as readonly string[]).includes(campo)) {
      campos.push(campo); // ni siquiera el creador
    } else if (esAutor) {
      continue;
    } else if ((CAMPOS_REQUERIMIENTO as readonly string[]).includes(campo)) {
      campos.push(campo);
    } else if (campo === CAMPO_RESPONSABLE) {
      const asignarseASiMismo = valor === usuario.id;
      const soltarLaPropia = valor === null && tarea.responsable?.id === usuario.id;
      if (!asignarseASiMismo && !soltarLaPropia) campos.push(campo);
    }
  }
  return campos.length === 0 ? { ok: true } : { ok: false, campos };
}
